import React, { useState } from "react";
import { Alert, Box, Checkbox, TextField, Typography } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import PrintIcon from "@mui/icons-material/Print";

import {
  IPD_COLORS,
  IPD_SHAPE,
  ipdElevatedInputSx,
  ipdLabelSx,
  IpdButton,
  IpdSectionCard,
} from "../../ipd/utils";
import { formatDateTime } from "../constants";
import { issueDeathCertificate, setDeathChecklist } from "../api/emergencyApi";
import { detailBlock, printDocument } from "../utils/print";

/**
 * Deceased checklist and death certificate (emergency-death-registration, ticket 04).
 *
 * The checklist is a focused subset — death certified (driven by certification),
 * body shifted (by the body release) and death summary shared. The registrar's
 * certificate is issued for a natural death and withheld pending post-mortem for
 * a medico-legal one.
 */

const MANNER_LABEL = {
  natural: "Natural",
  accident: "Accident",
  suicide: "Suicide",
  homicide: "Homicide",
  pending_investigation: "Pending investigation",
};

function ChecklistRow({ done, label, children }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      {done ? (
        <CheckCircleIcon sx={{ fontSize: 17, color: IPD_COLORS.success.text }} />
      ) : (
        <RadioButtonUncheckedIcon sx={{ fontSize: 17, color: IPD_COLORS.textMuted }} />
      )}
      <Typography sx={{ fontSize: 13.5, flex: 1, color: done ? IPD_COLORS.textPrimary : IPD_COLORS.textSecondary }}>
        {label}
      </Typography>
      {children}
    </Box>
  );
}

export default function DeathCertificateCard({ visit, onChanged }) {
  const record = visit.death_record;

  const [number, setNumber] = useState("");
  const [docRef, setDocRef] = useState("");
  const [issuing, setIssuing] = useState(false);
  const [summarySaving, setSummarySaving] = useState(false);
  const [error, setError] = useState("");

  if (!record) return null;

  const checklist = record.checklist || {};

  const toggleSummary = async (value) => {
    setSummarySaving(true);
    setError("");
    try {
      await setDeathChecklist(visit.id, { deathSummaryShared: value });
      await onChanged?.();
    } catch (e) {
      setError(e?.message || "Could not update the checklist");
    } finally {
      setSummarySaving(false);
    }
  };

  const issue = async () => {
    if (!number.trim()) return;
    setIssuing(true);
    setError("");
    try {
      await issueDeathCertificate(visit.id, {
        certificateNumber: number.trim(),
        certificateDocument: docRef.trim() || undefined,
      });
      await onChanged?.();
    } catch (e) {
      setError(e?.message || "Could not issue the death certificate");
    } finally {
      setIssuing(false);
    }
  };

  const printCertificate = () => {
    const opened = printDocument({
      title: "Death Certificate",
      subtitle: `${visit.display_name}${visit.visit_number ? ` · ${visit.visit_number}` : ""}`,
      body: detailBlock([
        ["Certificate number", record.certificate_number],
        ["Patient", visit.display_name],
        ["Hospital identifier", visit.patient_id],
        ["Visit number", visit.visit_number],
        ["Time of death", record.death_datetime ? formatDateTime(record.death_datetime) : ""],
        ["Cause of death", record.cause_of_death],
        ["Manner of death", MANNER_LABEL[record.manner_of_death] || record.manner_of_death],
        ["Certified by", record.certified_by_name ? `Dr. ${record.certified_by_name}` : ""],
        ["Issued", record.certificate_issued_at ? formatDateTime(record.certificate_issued_at) : ""],
      ]),
    });
    if (!opened) setError("The print window was blocked — allow pop-ups for this site.");
  };

  return (
    <IpdSectionCard
      title="Death certificate & checklist"
      subtitle="What closes out the death — the checklist and the registrar's certificate"
    >
      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: `${IPD_SHAPE.radiusMd}px` }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75, mb: 2 }}>
        <ChecklistRow done={checklist.death_certified} label="Death certified by the attending doctor" />
        <ChecklistRow done={checklist.body_shifted} label="Body shifted (mortuary / police / family)" />
        <ChecklistRow done={checklist.death_summary_shared} label="Death summary shared with the family">
          <Checkbox
            checked={Boolean(checklist.death_summary_shared)}
            disabled={summarySaving}
            onChange={(e) => toggleSummary(e.target.checked)}
            size="small"
            inputProps={{ "aria-label": "Death summary shared" }}
            sx={{ color: IPD_COLORS.textMuted, "&.Mui-checked": { color: IPD_COLORS.primary } }}
          />
        </ChecklistRow>
      </Box>

      {/* Certificate */}
      {record.certificate_withheld ? (
        <Alert severity="info" sx={{ borderRadius: `${IPD_SHAPE.radiusMd}px` }}>
          The death certificate is withheld pending post-mortem — a medico-legal death is not
          certified here.
        </Alert>
      ) : record.certificate_number ? (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            px: 1.75,
            py: 1.25,
            border: `1px solid ${IPD_COLORS.success.border}`,
            bgcolor: IPD_COLORS.success.bg,
            borderRadius: `${IPD_SHAPE.radiusMd}px`,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: 13.5, fontWeight: 700 }}>
              Certificate {record.certificate_number}
            </Typography>
            <Typography sx={{ fontSize: 12, color: IPD_COLORS.textSecondary }}>
              Issued {record.certificate_issued_at ? formatDateTime(record.certificate_issued_at) : ""}
              {record.certificate_issued_by_name ? ` · ${record.certificate_issued_by_name}` : ""}
            </Typography>
          </Box>
          <IpdButton variant="outlined" size="small" startIcon={<PrintIcon />} onClick={printCertificate}>
            Print
          </IpdButton>
        </Box>
      ) : (record.incomplete || []).length > 0 ? (
        <Alert severity="info" sx={{ borderRadius: `${IPD_SHAPE.radiusMd}px` }}>
          Complete the death record before issuing the certificate — still missing:{" "}
          {record.incomplete.join(", ")}.
        </Alert>
      ) : (
        <Box sx={{ display: "grid", gap: 1.5 }}>
          <Typography sx={{ ...ipdLabelSx }}>Issue the death certificate</Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.5 }}>
            <TextField
              value={number}
              onChange={(e) => setNumber(e.target.value)}
              size="small"
              placeholder="Certificate number"
              sx={ipdElevatedInputSx}
              inputProps={{ "aria-label": "Certificate number" }}
            />
            <TextField
              value={docRef}
              onChange={(e) => setDocRef(e.target.value)}
              size="small"
              placeholder="Document reference (optional)"
              sx={ipdElevatedInputSx}
              inputProps={{ "aria-label": "Certificate document reference" }}
            />
          </Box>
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <IpdButton onClick={issue} loading={issuing} disabled={!number.trim()}>
              Issue certificate
            </IpdButton>
          </Box>
        </Box>
      )}
    </IpdSectionCard>
  );
}
